import { useState } from 'react';
import { cn } from '../lib/cn';
import { MapDetailPanel } from './MapDetailPanel';
import { MapGrid } from './MapGrid';
import { MapCompactLegend, MapLegend } from './MapLegend';
import type { ArgentinaMapLabels, MapJurisdiction, MapStatus } from './types';

export interface ArgentinaMapProps {
  jurisdictions: readonly MapJurisdiction[];
  labels: ArgentinaMapLabels;
  /**
   * Called when a cell is clicked. When absent the cells stay hover/focus
   * targets only (the site's declarative map); when present the map is a
   * selector and every jurisdiction is selectable — including ones without
   * content, which is a decision, not an accident (requirement 016).
   */
  onSelect?: (jurisdiction: MapJurisdiction) => void;
  /** Smaller cells, no side panel, legend below. Used in marketing homes. */
  compact?: boolean;
}

/**
 * ArgentinaMap — schematic federal grid (5 cols x 10 rows, north to south,
 * west to east). The negative space draws Argentina's silhouette; every
 * jurisdiction is an equally legible, focusable cell whose status is a fill
 * color. Purely presentational: data, strings and callbacks arrive as props
 * (`./types`), the default geometry lives in `./grid`.
 *
 * a11y: cells are real <button>s with aria-label "Name — Status". Hover and
 * focus are kept in sync; the side panel is informational (role="status",
 * aria-live="polite"), not a dialog — there is nothing to dismiss.
 */
export function ArgentinaMap({ jurisdictions, labels, onSelect, compact = false }: ArgentinaMapProps) {
  const [active, setActive] = useState<MapJurisdiction | null>(null);
  const counts = countByStatus(jurisdictions);

  return (
    <div
      className={cn('grid items-start gap-8', !compact && 'lg:grid-cols-[1fr_280px] lg:gap-10')}
    >
      <MapGrid
        jurisdictions={jurisdictions}
        labels={labels}
        active={active}
        onEnter={setActive}
        onLeave={() => setActive(null)}
        onSelect={onSelect}
        compact={compact}
      />

      {compact ? (
        <MapCompactLegend labels={labels} counts={counts} />
      ) : (
        <aside className="space-y-6 lg:sticky lg:top-24">
          <MapDetailPanel active={active} labels={labels} />
          <MapLegend labels={labels} counts={counts} />
        </aside>
      )}
    </div>
  );
}

function countByStatus(jurisdictions: readonly MapJurisdiction[]): Record<MapStatus, number> {
  const counts: Record<MapStatus, number> = {
    covered: 0,
    partial: 0,
    progress: 0,
    citable: 0,
    pending: 0,
  };
  for (const jurisdiction of jurisdictions) counts[jurisdiction.status] += 1;
  return counts;
}
